import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup, FormSection } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { createParty, updateParty, fetchPartySisGroups, type Party } from '@/api/parties';
import { fetchStates } from '@/api/masters';
import { apiError } from '@/lib/apiError';
import {
  RX_EMAIL,
  RX_GSTIN,
  RX_IFSC,
  RX_MOBILE,
  RX_PAN,
  RX_PIN,
  detectStateFromGstin,
} from '@/lib/validation';

interface Props {
  open: boolean;
  editing: Party | null;
  onClose: () => void;
  /** Simplified form for the Lead Master page — hides ledger/tax/credit/bank
   *  fields and forces the party to a Lead-type customer. */
  leadMode?: boolean;
}

type FormState = {
  code: string;
  name: string;
  alias: string;
  isCustomer: boolean;
  isSupplier: boolean;
  isContractor: boolean;
  isIncomeExpense: boolean;
  ledger: string;
  openingBalance: string;
  drCr: 'Dr' | 'Cr';
  gstin: string;
  pan: string;
  contactPerson: string;
  mobile: string;
  whatsapp: string;
  email: string;
  telephone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateCode: string;
  pin: string;
  country: string;
  customerType: 'Wholesaler' | 'Distributor' | 'Retailer' | 'MBO & SiS' | 'Corporate & Projects' | 'Architect' | 'Customization' | 'Lead' | '';
  friendsFamilyDiscount?: string;
  region?: string;
  sisGroup: string;
  creditLimit: string;
  paymentTermDays: string;
  transport: string;
  courier: string;
  bankName: string;
  bankAccount: string;
  ifsc: string;
  reference: string;
};

function emptyForm(): FormState {
  return {
    code: '', name: '', alias: '',
    isCustomer: true, isSupplier: false, isContractor: false, isIncomeExpense: false,
    ledger: 'Sundry Debtors', openingBalance: '', drCr: 'Dr',
    gstin: '', pan: '', contactPerson: '', mobile: '', whatsapp: '', email: '', telephone: '',
    addressLine1: '', addressLine2: '', city: '', stateCode: '', pin: '', country: 'India',
    customerType: '', sisGroup: '', creditLimit: '', paymentTermDays: '',
    transport: '', courier: '', bankName: '', bankAccount: '', ifsc: '', reference: '',
  };
}

function fromParty(p: Party): FormState {
  return {
    code: p.code,
    name: p.name,
    alias: p.alias ?? '',
    isCustomer: p.isCustomer,
    isSupplier: p.isSupplier,
    isContractor: p.isContractor,
    isIncomeExpense: p.isIncomeExpense,
    ledger: p.ledger,
    openingBalance: p.openingBalance,
    drCr: p.drCr,
    gstin: p.gstin ?? '',
    pan: p.pan ?? '',
    contactPerson: p.contactPerson ?? '',
    mobile: p.mobile ?? '',
    whatsapp: p.whatsapp ?? '',
    email: p.email ?? '',
    telephone: p.telephone ?? '',
    addressLine1: p.addressLine1 ?? '',
    addressLine2: p.addressLine2 ?? '',
    city: p.city ?? '',
    stateCode: p.stateCode ?? '',
    pin: p.pin ?? '',
    country: p.country ?? 'India',
    customerType: p.customerType ?? '',
    friendsFamilyDiscount: p.friendsFamilyDiscount ?? undefined,
    region: p.region ?? undefined,
    sisGroup: p.sisGroup ?? '',
    creditLimit: p.creditLimit ?? '',
    paymentTermDays: p.paymentTermDays != null ? String(p.paymentTermDays) : '',
    transport: p.transport ?? '',
    courier: p.courier ?? '',
    bankName: p.bankName ?? '',
    bankAccount: p.bankAccount ?? '',
    ifsc: p.ifsc ?? '',
    reference: p.reference ?? '',
  };
}

function toPayload(f: FormState): Record<string, unknown> {
  const out: Record<string, unknown> = {
    name: f.name.trim(),
    isCustomer: f.isCustomer,
    isSupplier: f.isSupplier,
    isContractor: f.isContractor,
    isIncomeExpense: f.isIncomeExpense,
    ledger: f.ledger,
    drCr: f.drCr,
    country: f.country,
  };
  if (f.code.trim()) out.code = f.code.trim().toUpperCase();
  const optStr: Array<[keyof FormState, string]> = [
    ['alias', 'alias'], ['gstin', 'gstin'], ['pan', 'pan'],
    ['contactPerson', 'contactPerson'], ['mobile', 'mobile'], ['whatsapp', 'whatsapp'],
    ['email', 'email'], ['telephone', 'telephone'],
    ['addressLine1', 'addressLine1'], ['addressLine2', 'addressLine2'],
    ['city', 'city'], ['stateCode', 'stateCode'], ['pin', 'pin'],
    ['transport', 'transport'], ['courier', 'courier'],
    ['bankName', 'bankName'], ['bankAccount', 'bankAccount'], ['ifsc', 'ifsc'],
    ['reference', 'reference'],
  ];
  for (const [k, target] of optStr) {
    const v = (f[k] as string).trim();
    if (v) out[target] = v;
  }
  const optNum: Array<[keyof FormState, string]> = [
    ['openingBalance', 'openingBalance'],
    ['friendsFamilyDiscount', 'friendsFamilyDiscount'],
    ['creditLimit', 'creditLimit'],
    ['paymentTermDays', 'paymentTermDays'],
  ];
  for (const [k, target] of optNum) {
    const raw = ((f[k] as string | undefined) ?? '').trim();
    if (raw !== '') out[target] = Number(raw);
  }
  // Customer type (and the SIS group) only apply when the party is a customer —
  // the field is hidden otherwise, so a stale choice must not be saved for a vendor.
  if (f.isCustomer && f.customerType) out.customerType = f.customerType;
  // Group / company applies only to SIS stores.
  if (f.isCustomer && f.customerType === 'MBO & SiS' && f.sisGroup.trim()) out.sisGroup = f.sisGroup.trim();
  return out;
}

function validate(f: FormState, leadMode = false): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!f.name.trim()) errs.name = 'Required';
  if (!f.isCustomer && !f.isSupplier && !f.isContractor && !f.isIncomeExpense) {
    errs.type = 'Select at least one party type';
  }
  if (f.gstin && !RX_GSTIN.test(f.gstin)) errs.gstin = 'Format: 22AAAAA0000A1Z5';
  if (f.pan && !RX_PAN.test(f.pan)) errs.pan = 'Format: AAAAA1234A';
  if (f.mobile && !RX_MOBILE.test(f.mobile)) errs.mobile = '10 digits';
  if (f.whatsapp && !RX_MOBILE.test(f.whatsapp)) errs.whatsapp = '10 digits';
  if (f.email && !RX_EMAIL.test(f.email)) errs.email = 'Invalid email';
  if (f.pin && !RX_PIN.test(f.pin)) errs.pin = '6 digits';
  if (f.ifsc && !RX_IFSC.test(f.ifsc)) errs.ifsc = '11 chars, e.g. SBIN0001234';
  // Customer Type + F&F discount are collected at Convert time for leads, so
  // they aren't required (or shown) in Lead mode.
  if (!leadMode && f.isCustomer && !f.customerType) errs.customerType = 'Required for customers';
  if (!leadMode && f.isCustomer && (f.friendsFamilyDiscount === undefined || f.friendsFamilyDiscount === '')) errs.friendsFamilyDiscount = 'Required for customers';
  return errs;
}

export function PartyFormModal({ open, editing, onClose, leadMode = false }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const initialForm = useRef<FormState>(emptyForm());

  const isDirty = !!editing && JSON.stringify(form) !== JSON.stringify(initialForm.current);
  const [sameAsWhatsapp, setSameAsWhatsapp] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial = editing ? fromParty(editing) : emptyForm();
    // A new lead is a Lead-type customer; F&F discount is set later on Convert.
    if (leadMode && !editing) { initial.customerType = 'Lead'; initial.friendsFamilyDiscount = '0'; }
    setForm(initial);
    initialForm.current = initial;
    setErrors({});
    setSameAsWhatsapp(false);
  }, [open, editing, leadMode]);

  useEffect(() => {
    if (sameAsWhatsapp) {
      setForm((s) => ({ ...s, whatsapp: s.mobile }));
    }
  }, [sameAsWhatsapp, form.mobile]);

  // When validation fails, bring the first offending field into view so the
  // "fix the highlighted errors" message always points at something visible.
  useEffect(() => {
    if (Object.keys(errors).length === 0) return;
    const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus?.({ preventScroll: true });
  }, [errors]);

  const { data: states = [] } = useQuery({ queryKey: ['states'], queryFn: fetchStates });
  const { data: sisGroups = [] } = useQuery({
    queryKey: ['party-sis-groups'],
    queryFn: fetchPartySisGroups,
    enabled: open,
  });

  // Auto-detect state when GSTIN entered.
  useEffect(() => {
    if (!form.gstin) return;
    const detected = detectStateFromGstin(form.gstin);
    if (detected && form.stateCode !== detected.code) {
      setForm((s) => ({ ...s, stateCode: detected.code }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.gstin]);

  const editingId = editing?.id ?? null;
  const title = leadMode
    ? (editingId ? `Edit Lead — ${editing?.name}` : 'New Lead')
    : (editingId ? `Edit Party — ${editing?.name}` : 'Add Party');

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = toPayload(form);
      return editingId ? updateParty(editingId, payload) : createParty(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parties'] });
      toast.push(editingId ? 'Party updated' : 'Party created', 'success');
      initialForm.current = form; // reset dirty after save
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  function onSubmit() {
    const errs = validate(form, leadMode);
    setErrors(errs);
    if (Object.keys(errs).length === 0) saveMutation.mutate();
    else toast.push('Please fix the highlighted errors', 'error');
  }

  async function handleClose() {
    if (isDirty && !await confirmDlg('You have unsaved changes. Discard and close?')) return;
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          {isDirty ? (
            <span className="flex items-center gap-1.5 rounded-full bg-warning/15 px-2.5 py-1 text-[11px] font-semibold text-warning">
              ● Unsaved changes
            </span>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="glass" onClick={handleClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={saveMutation.isPending || (!!editing && !isDirty)}
            >
              {saveMutation.isPending
                ? 'Saving…'
                : editing
                  ? isDirty ? 'Save Changes' : 'No Changes'
                  : leadMode ? 'Save Lead' : 'Save Party'}
            </Button>
          </div>
        </div>
      }
    >
      {/* Type radio buttons — hidden in Lead mode (a lead is always a customer prospect) */}
      {!leadMode && (
        <>
      <div className="mb-4 flex flex-wrap gap-5 rounded-lg bg-white/[0.04] p-3.5">
        {(['isCustomer', 'isSupplier', 'isContractor', 'isIncomeExpense'] as const).map((k) => (
          <label key={k} className="flex cursor-pointer items-center gap-2 text-sm font-bold">
            <input
              type="radio"
              name="partyType"
              checked={form[k]}
              onChange={() => setForm({
                ...form,
                isCustomer: k === 'isCustomer',
                isSupplier: k === 'isSupplier',
                isContractor: k === 'isContractor',
                isIncomeExpense: k === 'isIncomeExpense',
              })}
            />
            {k === 'isCustomer' ? 'Customer'
              : k === 'isSupplier' ? 'Vendor / Supplier'
              : k === 'isContractor' ? 'Contractor'
              : 'Income / Expense'}
          </label>
        ))}
      </div>
      {errors.type && <div className="mb-3 text-xs text-danger">{errors.type}</div>}
        </>
      )}

      {/* Customer classification — hidden in Lead mode */}
      {!leadMode && form.isCustomer && (
        <div className="mb-4 rounded-card border border-indigo-300/20 bg-indigo-400/[0.06] p-3.5">
          <FormGroup label="Customer Type" required error={errors.customerType}>
            <Select
              invalid={!!errors.customerType}
              value={form.customerType}
              onChange={(e) =>
                setForm({ ...form, customerType: e.target.value as FormState['customerType'] })
              }
            >
              <option value="">-- Select --</option>
              <option>Wholesaler</option>
              <option>Distributor</option>
              <option>Retailer</option>
              <option>MBO &amp; SiS</option>
              <option>Corporate &amp; Projects</option>
              <option>Architect</option>
              <option>Customization</option>
            </Select>
          </FormGroup>
          {form.customerType === 'MBO & SiS' && (
            <div className="mt-3">
              <FormGroup label="Group / Company" hint="Parent chain — stores are grouped by this on the SIS pages">
                <Input
                  list="sis-group-options"
                  value={form.sisGroup}
                  onChange={(e) => setForm({ ...form, sisGroup: e.target.value })}
                  placeholder="e.g. Suvidha"
                />
                <datalist id="sis-group-options">
                  {sisGroups.map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
              </FormGroup>
            </div>
          )}
        </div>
      )}

      <FormSection title="Identity">
        <div className="grid grid-cols-3 gap-3">
          <FormGroup label="Party Code" hint={editingId ? '' : 'Auto if blank'}>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="Auto"
              disabled={!!editingId}
            />
          </FormGroup>
          <FormGroup label="Party Name" required error={errors.name}>
            <Input invalid={!!errors.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormGroup>
          {!leadMode && (
          <>
          <FormGroup label="Alias">
            <Input value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} />
          </FormGroup>
          <FormGroup label="Under Ledger">
            <Select
              value={form.ledger}
              onChange={(e) => setForm({ ...form, ledger: e.target.value })}
            >
              <option>Sundry Debtors</option>
              <option>Sundry Creditors</option>
              <option>Cash Account</option>
              <option>Bank Account</option>
            </Select>
          </FormGroup>
          <FormGroup label="Opening Balance">
            <Input
              type="number"
              value={form.openingBalance}
              onChange={(e) => setForm({ ...form, openingBalance: e.target.value })}
            />
          </FormGroup>
          <FormGroup label="Dr / Cr">
            <Select
              value={form.drCr}
              onChange={(e) => setForm({ ...form, drCr: e.target.value as 'Dr' | 'Cr' })}
            >
              <option>Dr</option>
              <option>Cr</option>
            </Select>
          </FormGroup>
          </>
          )}
        </div>
      </FormSection>

      {!leadMode && form.isCustomer && (
        <div className="mb-4">
          <FormGroup label="Friends &amp; Family Discount %" required error={errors.friendsFamilyDiscount} hint="Extra discount applied on invoice for this customer">
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              invalid={!!errors.friendsFamilyDiscount}
              value={form.friendsFamilyDiscount ?? ''}
              onChange={(e) => setForm({ ...form, friendsFamilyDiscount: e.target.value || undefined })}
              placeholder="0.00"
              className="font-mono"
              required
            />
          </FormGroup>
        </div>
      )}

      {!leadMode && (
      <FormSection title="Tax IDs">
        <div className="grid grid-cols-2 gap-3">
          <FormGroup label="GSTIN" error={errors.gstin}>
            <Input
              invalid={!!errors.gstin}
              value={form.gstin}
              onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
            />
            {form.gstin && detectStateFromGstin(form.gstin) && (
              <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                Auto-detected state: {detectStateFromGstin(form.gstin)?.name}
              </div>
            )}
          </FormGroup>
          <FormGroup label="PAN" error={errors.pan}>
            <Input
              invalid={!!errors.pan}
              value={form.pan}
              onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
              placeholder="AAAAA0000A"
              maxLength={10}
            />
          </FormGroup>
        </div>
      </FormSection>
      )}

      <FormSection title="Contact">
        <div className="grid grid-cols-3 gap-3">
          <FormGroup label="Contact Person">
            <Input
              value={form.contactPerson}
              onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
            />
          </FormGroup>
          <FormGroup label="Mobile" error={errors.mobile}>
            <Input
              invalid={!!errors.mobile}
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              placeholder="10 digits"
              maxLength={10}
            />
          </FormGroup>
          <FormGroup label="WhatsApp" error={errors.whatsapp}>
            <Input
              invalid={!!errors.whatsapp}
              value={form.whatsapp}
              onChange={(e) => { if (!sameAsWhatsapp) setForm({ ...form, whatsapp: e.target.value }); }}
              placeholder="10 digits"
              maxLength={10}
              disabled={sameAsWhatsapp}
            />
            <label className="flex items-center gap-1.5 mt-1 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sameAsWhatsapp}
                onChange={(e) => setSameAsWhatsapp(e.target.checked)}
                className="accent-accent w-3 h-3"
              />
              <span className="text-[11px] text-[var(--text-secondary)]">Same as Mobile</span>
            </label>
          </FormGroup>
          <FormGroup label="Email" error={errors.email}>
            <Input
              type="email"
              invalid={!!errors.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </FormGroup>
          <FormGroup label="Telephone">
            <Input
              value={form.telephone}
              onChange={(e) => setForm({ ...form, telephone: e.target.value })}
            />
          </FormGroup>
          <FormGroup label="Reference">
            <Input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
            />
          </FormGroup>
        </div>
      </FormSection>

      <FormSection title="Address">
        <div className="grid grid-cols-2 gap-3">
          <FormGroup label="Address Line 1" className="col-span-2">
            <Input value={form.addressLine1} onChange={(e) => setForm({ ...form, addressLine1: e.target.value })} />
          </FormGroup>
          <FormGroup label="Address Line 2" className="col-span-2">
            <Input value={form.addressLine2} onChange={(e) => setForm({ ...form, addressLine2: e.target.value })} />
          </FormGroup>
          <FormGroup label="City">
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </FormGroup>
          <FormGroup label="State">
            <Select
              value={form.stateCode}
              onChange={(e) => setForm({ ...form, stateCode: e.target.value })}
            >
              <option value="">-- Select --</option>
              {states.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="PIN Code" error={errors.pin}>
            <Input
              invalid={!!errors.pin}
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value })}
              maxLength={6}
            />
          </FormGroup>
          <FormGroup label="Country">
            <Select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}>
              <option>India</option>
              <option>Nepal</option>
              <option>Bangladesh</option>
              <option>Other</option>
            </Select>
          </FormGroup>
        </div>
      </FormSection>

      {!leadMode && (form.isCustomer || form.isSupplier) && (
        <FormSection title="Credit & Logistics">
          <div className="grid grid-cols-3 gap-3">
            <FormGroup label="Credit Limit">
              <Input
                type="number"
                value={form.creditLimit}
                onChange={(e) => setForm({ ...form, creditLimit: e.target.value })}
              />
            </FormGroup>
            <FormGroup label="Payment Terms (Days)">
              <Input
                type="number"
                value={form.paymentTermDays}
                onChange={(e) => setForm({ ...form, paymentTermDays: e.target.value })}
              />
            </FormGroup>
            <FormGroup label="Transport">
              <Input
                value={form.transport}
                onChange={(e) => setForm({ ...form, transport: e.target.value })}
              />
            </FormGroup>
            <FormGroup label="Courier">
              <Input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} />
            </FormGroup>
          </div>
        </FormSection>
      )}

      {!leadMode && (
      <FormSection title="Bank">
        <div className="grid grid-cols-3 gap-3">
          <FormGroup label="Bank Name">
            <Input value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} />
          </FormGroup>
          <FormGroup label="Bank A/C No">
            <Input
              value={form.bankAccount}
              onChange={(e) => setForm({ ...form, bankAccount: e.target.value })}
            />
          </FormGroup>
          <FormGroup label="IFSC" error={errors.ifsc}>
            <Input
              invalid={!!errors.ifsc}
              value={form.ifsc}
              onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })}
              placeholder="SBIN0001234"
              maxLength={11}
            />
          </FormGroup>
        </div>
      </FormSection>
      )}
    </Modal>
  );
}
